/**
 * Projections API service
 *
 * Handles all API calls for the Projections feature including:
 * - Getting projection data with historical comparisons
 * - Saving projection inputs
 * - Edit lock management
 * - Executive override (for editing outside the window)
 */

import { ApiResponse, MonthlyTotals } from '../types';
import {
  ProjectionData,
  SaveProjectionRequest,
  LockRequest,
  LockOperationResponse,
  OverrideRequest,
  OverrideResponse,
  ProjectionInputRecord,
  ProjectionCalculatedRecord,
  EditLockInfo,
} from '../types/projections';
import { ProjectionViewData } from '../types/projectionViewData';
import { VarianceResponse } from '../types/variance';

const API_URL = process.env.REACT_APP_API_URL || '';

// Token getter function - will be set by AuthContext (shared with main api.ts)
// Accepts optional forceRefresh parameter to bypass MSAL cache
let getAccessTokenFn: ((forceRefresh?: boolean) => Promise<string | null>) | null = null;

/**
 * Set the token getter function (called from AuthContext)
 */
export function setTokenGetter(fn: (forceRefresh?: boolean) => Promise<string | null>) {
  getAccessTokenFn = fn;
}

// Track if we're currently refreshing token to avoid multiple refresh attempts
let isRefreshingToken = false;
let tokenRefreshPromise: Promise<string | null> | null = null;

/**
 * Force refresh the token (bypasses MSAL cache and gets new token from Azure AD)
 */
async function forceRefreshToken(): Promise<string | null> {
  if (isRefreshingToken && tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  isRefreshingToken = true;
  tokenRefreshPromise = (async () => {
    try {
      if (getAccessTokenFn) {
        // Pass forceRefresh=true to bypass MSAL's token cache
        return await getAccessTokenFn(true);
      }
      return null;
    } finally {
      isRefreshingToken = false;
      tokenRefreshPromise = null;
    }
  })();

  return tokenRefreshPromise;
}

/**
 * Generic API request handler with error handling and authentication
 * Automatically retries with fresh token on 401/403 errors
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry: boolean = false
): Promise<ApiResponse<T>> {
  try {
    // Get access token if available
    let authHeaders: Record<string, string> = {};
    if (getAccessTokenFn) {
      const token = await getAccessTokenFn();
      if (token) {
        authHeaders['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle 401/403 - token may be expired
      if ((response.status === 401 || response.status === 403) && !isRetry) {
        console.warn('Token may be expired, attempting refresh...');
        const newToken = await forceRefreshToken();
        if (newToken) {
          // Retry the request with the new token
          return apiRequest<T>(endpoint, options, true);
        }
        // If we couldn't refresh, let the error propagate
        console.error('Authentication required - token refresh failed');
      }
      throw new Error(data.error || `HTTP error ${response.status}`);
    }

    return data;
  } catch (error: any) {
    console.error(`API request failed: ${endpoint}`, error);
    return {
      success: false,
      error: error.message || 'Network error occurred',
    };
  }
}

// ============================================
// PROJECTION DATA
// ============================================

/**
 * Options for getProjections API call
 */
export interface GetProjectionsOptions {
  locationId: number;
  targetYear: number;
  /**
   * When true, includes pre-computed view-ready data in the response.
   * This enables the frontend to skip complex data processing.
   */
  includeViewData?: boolean;
}

/**
 * Extended projection data with optional view data
 */
export interface ProjectionDataWithViewData extends ProjectionData {
  /** Pre-computed view data (only present if includeViewData=true) */
  viewData?: ProjectionViewData;
}

/**
 * Get projection data for a location and target year.
 * Returns inputs, calculations, metadata, historical data, and lock status.
 *
 * @param options - Configuration options including locationId, targetYear, and optional includeViewData
 */
export async function getProjections(
  options: GetProjectionsOptions
): Promise<ApiResponse<ProjectionDataWithViewData>>;

/**
 * Get projection data for a location and target year.
 * Returns inputs, calculations, metadata, historical data, and lock status.
 *
 * @deprecated Use the options object overload instead: getProjections({ locationId, targetYear, includeViewData })
 */
export async function getProjections(
  locationId: number,
  targetYear: number
): Promise<ApiResponse<ProjectionData>>;

/**
 * Get projection data for a location and target year.
 * Returns inputs, calculations, metadata, historical data, and lock status.
 */
export async function getProjections(
  locationIdOrOptions: number | GetProjectionsOptions,
  targetYear?: number
): Promise<ApiResponse<ProjectionDataWithViewData>> {
  // Handle both overloads
  let locationId: number;
  let year: number;
  let includeViewData = false;

  if (typeof locationIdOrOptions === 'object') {
    locationId = locationIdOrOptions.locationId;
    year = locationIdOrOptions.targetYear;
    includeViewData = locationIdOrOptions.includeViewData ?? false;
  } else {
    locationId = locationIdOrOptions;
    year = targetYear!;
  }

  let url = `/projections?locationId=${locationId}&targetYear=${year}`;
  if (includeViewData) {
    url += '&includeViewData=true';
  }

  return apiRequest<ProjectionDataWithViewData>(url);
}

/**
 * Save projection inputs.
 * Requires user to hold the edit lock.
 * Backend will calculate seasonality distribution.
 */
export interface SaveProjectionResponse {
  success: boolean;
  message: string;
  inputs: ProjectionInputRecord[];
  calculations: ProjectionCalculatedRecord[];
  // Executive monthly adjustments this save cleared because the annual changed
  droppedAdjustments?: DroppedAdjustment[];
}

export interface DroppedAdjustment {
  department: string;
  metric: string;
  adjustedByName: string;
  reason: string;
}

/**
 * PUT /projections/monthly-adjustment (Admin/CEO/Executive): move dollars or units between
 * months of one saved CALCULATED record without changing its annual. `monthlyValues` are the
 * 12 absolute targets; `reset` restores the seasonality spread. `expectedAdjustedAt` is the
 * adjustedAt the client last saw (null when the row had none) and yields a 409 on a race.
 */
export interface MonthlyAdjustmentRequest {
  locationId: number;
  targetYear: number;
  department: string;
  metric: string;
  monthlyValues?: MonthlyTotals;
  reset?: boolean;
  expectedAdjustedAt: string | null;
}

export async function adjustProjectionMonths(
  request: MonthlyAdjustmentRequest
): Promise<ApiResponse<{ calculations: ProjectionCalculatedRecord[] }>> {
  return apiRequest('/projections/monthly-adjustment', {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

export async function saveProjection(
  request: SaveProjectionRequest
): Promise<ApiResponse<SaveProjectionResponse>> {
  return apiRequest<SaveProjectionResponse>('/projections', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// ============================================
// EDIT LOCK MANAGEMENT
// ============================================

/**
 * Acquire an edit lock for a projection.
 * Returns success if lock acquired, or error if already locked by another user.
 */
export async function acquireEditLock(
  locationId: number,
  targetYear: number
): Promise<ApiResponse<LockOperationResponse>> {
  return apiRequest<LockOperationResponse>('/projections/lock', {
    method: 'POST',
    body: JSON.stringify({ locationId, targetYear } as LockRequest),
  });
}

/**
 * Release an edit lock.
 * Only the lock holder can release (unless Executive force-release).
 */
export async function releaseEditLock(
  locationId: number,
  targetYear: number
): Promise<ApiResponse<LockOperationResponse>> {
  return apiRequest<LockOperationResponse>(
    `/projections/lock?locationId=${locationId}&targetYear=${targetYear}`,
    { method: 'DELETE' }
  );
}

/**
 * Extend the current user's edit lock.
 * Called periodically to prevent lock expiration while editing.
 */
export async function extendEditLock(
  locationId: number,
  targetYear: number
): Promise<ApiResponse<LockOperationResponse>> {
  return apiRequest<LockOperationResponse>('/projections/lock/extend', {
    method: 'POST',
    body: JSON.stringify({ locationId, targetYear } as LockRequest),
  });
}

/**
 * Get current lock status without attempting to acquire.
 */
export async function getLockStatus(
  locationId: number,
  targetYear: number
): Promise<ApiResponse<{ lockStatus: EditLockInfo }>> {
  return apiRequest<{ lockStatus: EditLockInfo }>(
    `/projections/lock/status?locationId=${locationId}&targetYear=${targetYear}`
  );
}

// ============================================
// EXECUTIVE OVERRIDE (for editing outside window)
// ============================================

/**
 * Request a 24-hour editing override (Executive only).
 * Used when editing window (Oct 1 - Dec 31, ET) has closed but changes are needed.
 * Does NOT change projection status - status is determined by date.
 */
export async function requestEditOverride(
  request: OverrideRequest
): Promise<ApiResponse<OverrideResponse>> {
  return apiRequest<OverrideResponse>('/projections/unlock', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Unlock an archived (past-year) projection for 24 hours (Admin/CEO/Executive).
 * Grants the same METADATA override as requestEditOverride, plus an audited reason.
 */
export async function requestArchiveUnlock(request: {
  locationId: number;
  targetYear: number;
  reason: string;
}): Promise<ApiResponse<{ success: boolean; message?: string; expiresAt?: string }>> {
  return apiRequest('/projections/archive-unlock', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if a lock is expired
 */
export function isLockExpired(lockInfo: EditLockInfo): boolean {
  if (!lockInfo.isLocked || !lockInfo.expiresAt) {
    return false;
  }
  return new Date() > new Date(lockInfo.expiresAt);
}

/**
 * Get time remaining on a lock in minutes
 */
export function getLockTimeRemaining(lockInfo: EditLockInfo): number {
  if (!lockInfo.isLocked || !lockInfo.expiresAt) {
    return 0;
  }
  const remaining = new Date(lockInfo.expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(remaining / 60000));
}

/**
 * Format lock status message for display
 */
export function formatLockStatusMessage(lockInfo: EditLockInfo): string {
  if (!lockInfo.isLocked) {
    return 'Available for editing';
  }

  if (lockInfo.isCurrentUser) {
    const minutes = getLockTimeRemaining(lockInfo);
    return `You have the edit lock (${minutes} min remaining)`;
  }

  return `Locked by ${lockInfo.lockedByName || 'another user'}`;
}

// ============================================
// VARIANCE ANALYSIS
// ============================================

/**
 * Get variance data comparing projections vs actuals for a location and year.
 * Used for the Variance Analysis tab to show projected vs actual performance.
 */
export async function getVariance(
  locationId: number,
  year: number
): Promise<ApiResponse<VarianceResponse>> {
  return apiRequest<VarianceResponse>(
    `/projections/variance?locationId=${locationId}&year=${year}`
  );
}
